import React, { useState, useEffect } from "react";
import {
  PlusIcon,
  EyeIcon,
  PencilIcon,
  TrashIcon,
  MagnifyingGlassIcon,
} from "@heroicons/react/24/outline";
import { useAuth } from "../contexts/AuthContext";
import { ActivityDefinition } from "../types";
import { ActivityEditor } from "../components/activities/ActivityEditor";
import { PaginationControls } from "../components/common/PaginationControls";
import toast from "../utils/toast";
import axios from "axios";
import { useTranslation } from "react-i18next";

export const ActivitiesManagement: React.FC = () => {
  const { t } = useTranslation(["activities", "common", "errors"]);
  const { user } = useAuth();
  const [activities, setActivities] = useState<ActivityDefinition[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [showActivityEditor, setShowActivityEditor] = useState(false);
  const [editingActivity, setEditingActivity] =
    useState<ActivityDefinition | null>(null);
  const [viewingActivity, setViewingActivity] =
    useState<ActivityDefinition | null>(null);

  useEffect(() => {
    fetchActivities();
  }, []);

  const fetchActivities = async () => {
    try {
      setLoading(true);
      const activitiesRes = await axios.get("/api/activities");
      const activitiesList = (activitiesRes.data.data ||
        []) as ActivityDefinition[];

      // Sort by creation date
      const sorted = activitiesList.sort(
        (a, b) =>
          new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
      );

      setActivities(sorted);
    } catch (error) {
      console.error("Error fetching activities:", error);
      toast.error(t("errors:loadFailed"));
    } finally {
      setLoading(false);
    }
  };

  const filteredActivities = activities
    .filter(
      (activity) =>
        activity.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (activity.description &&
          activity.description.toLowerCase().includes(searchTerm.toLowerCase()))
    );

  const totalPages = Math.max(
    1,
    Math.ceil(filteredActivities.length / itemsPerPage)
  );
  const paginatedActivities = filteredActivities.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, itemsPerPage]);

  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, totalPages]);

  const handleCreateActivity = () => {
    setEditingActivity(null);
    setViewingActivity(null);
    setShowActivityEditor(true);
  };

  const handleEditActivity = (activity: ActivityDefinition) => {
    setEditingActivity(activity);
    setViewingActivity(null);
    setShowActivityEditor(true);
  };

  const handleViewActivity = (activity: ActivityDefinition) => {
    setViewingActivity(activity);
    setEditingActivity(null);
    setShowActivityEditor(true);
  };

  const handleSaveActivity = async (activityData: ActivityDefinition) => {
    try {
      if (editingActivity?.id) {
        // Update existing activity
        await axios.put(
          `/api/activities/${editingActivity.id}`,
          activityData
        );
        toast.success(t("activities:activityUpdated"));
      } else {
        // Create new activity
        await axios.post("/api/activities", activityData);
        toast.success(t("activities:activityCreated"));
      }
      setShowActivityEditor(false);
      setEditingActivity(null);
      setViewingActivity(null);
      fetchActivities();
    } catch (error) {
      console.error("Error saving activity:", error);
      toast.error(t("errors:saveFailed"));
    }
  };

  const handleCancelEditor = () => {
    setShowActivityEditor(false);
    setEditingActivity(null);
    setViewingActivity(null);
  };

  const handleDeleteActivity = async (activity: ActivityDefinition) => {
    if (!window.confirm(t("activities:confirmDelete", { itemType: t("activities:activity") }))) {
      return;
    }

    try {
      await axios.delete(`/api/activities/${activity.id}`);
      toast.success(t("activities:activityDeleted"));
      fetchActivities();
    } catch (error) {
      console.error("Error deleting:", error);
      toast.error(t("errors:deleteFailed"));
    }
  };

  if (loading) {
    return (
      <div className="app-page bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="app-page bg-gradient-to-br from-slate-50 via-blue-50/30 to-indigo-50 pt-14 pb-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-4 flex justify-stretch sm:justify-end">
          <button
            onClick={handleCreateActivity}
            className="inline-flex w-full items-center justify-center px-5 py-2.5 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-xl hover:from-blue-700 hover:to-indigo-700 shadow-md hover:shadow-lg transition-all font-medium group sm:w-auto"
          >
            <PlusIcon className="h-5 w-5 mr-2 group-hover:rotate-90 transition-transform" />
            {t("activities:createActivity")}
          </button>
        </div>

        {/* Search and Filters */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6">
          <div className="relative">
            <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder={t("activities:searchActivity")}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
        </div>

        {/* Activities Table */}
        <div className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-xl border border-white/20 overflow-hidden">
          {filteredActivities.length === 0 ? (
            <div className="p-12 text-center">
              <div className="text-gray-400 mb-4">
                <svg
                  className="mx-auto h-12 w-12"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
                  />
                </svg>
              </div>
              <h3 className="text-sm font-medium text-gray-900 mb-2">
                {t("activities:noActivities")}
              </h3>
              <p className="text-gray-600 mb-6">
                {searchTerm
                  ? t("activities:noSearchResults", {
                      itemType: t("activities:activity"),
                    })
                  : t("activities:createFirstActivity")}
              </p>
              {!searchTerm && (
                <button
                  onClick={handleCreateActivity}
                  className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
                >
                  <PlusIcon className="w-4 h-4 mr-2" />
                  {t("activities:createFirstActivity")}
                </button>
              )}
            </div>
          ) : (
            <>
              {/* Table Header */}
              <div className="hidden lg:block bg-gradient-to-r from-slate-50 to-blue-50/50 border-b border-slate-200 px-5 py-2.5">
                <div className="grid grid-cols-12 gap-4 text-xs font-semibold text-slate-600 uppercase tracking-wide">
                  <div className="col-span-5">{t("common:name")}</div>
                  <div className="col-span-2">{t("activities:formCount")}</div>
                  <div className="col-span-2">{t("activities:documents")}</div>
                  <div className="col-span-3 text-right">
                    {t("common:actions")}
                  </div>
                </div>
              </div>

              {/* Table Body */}
              <div className="divide-y divide-slate-100">
                {paginatedActivities.map((activity, index) => {
                  const activityIcons = [
                    "🎯",
                    "📋",
                    "⚡",
                    "🚀",
                    "💼",
                    "🔧",
                    "📊",
                    "💡",
                  ];
                  const activityIcon =
                    activityIcons[
                      ((currentPage - 1) * itemsPerPage + index) %
                        activityIcons.length
                    ];
                  const isArchived =
                    "isArchived" in activity && activity.isArchived;

                  let formCount = 0;
                  if ("activityForms" in activity) {
                    const activityDef = activity as ActivityDefinition;
                    formCount = activityDef.activityForms?.length || 0;
                  }

                  // Count attachments
                  let attachmentCount = 0;
                  if ("attachments" in activity && activity.attachments) {
                    try {
                      const attachmentsArray = JSON.parse(activity.attachments);
                      attachmentCount = Array.isArray(attachmentsArray)
                        ? attachmentsArray.length
                        : 0;
                    } catch (e) {
                      attachmentCount = 0;
                    }
                  }

                  return (
                    <div
                      key={activity.id}
                      className={`relative group hover:bg-blue-50/30 transition-all duration-200 ${
                        isArchived ? "opacity-60" : ""
                      }`}
                    >
                      <div className="px-4 lg:px-5 py-3">
                        {/* Desktop Layout */}
                        <div className="hidden lg:grid lg:grid-cols-12 gap-4 items-center">
                          {/* Title */}
                          <div className="col-span-5">
                            <div className="flex items-center space-x-3">
                              <div className="flex-shrink-0">
                                <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center shadow-sm">
                                  <span className="text-sm">
                                    {activityIcon}
                                  </span>
                                </div>
                              </div>
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2">
                                  <h3 className="text-xs font-bold text-slate-800 truncate group-hover:text-blue-700 transition-colors">
                                    {activity.title}
                                  </h3>
                                  {/* Status Badges - Inline with title */}
                                  {activity.requiresApproval && (
                                    <span
                                      className="inline-flex items-center px-2 py-0.5 text-xs font-semibold rounded-full bg-amber-100 text-amber-800"
                                      title={t("activities:requiresApproval")}
                                    >
                                      ✓
                                    </span>
                                  )}
                                  {isArchived && (
                                    <span
                                      className="inline-flex items-center px-2 py-0.5 text-xs font-semibold rounded-full bg-gray-100 text-gray-800"
                                      title={t("common:archive")}
                                    >
                                      📦
                                    </span>
                                  )}
                                </div>
                                {activity.description && (
                                  <p className="text-xs text-slate-500 truncate mt-1">
                                    {activity.description}
                                  </p>
                                )}
                              </div>
                            </div>
                          </div>

                          {/* Form Count */}
                          <div className="col-span-2">
                            <span className="inline-flex items-center px-3 py-1.5 text-sm font-semibold rounded-lg bg-blue-100 text-blue-800">
                              📋 {formCount}
                            </span>
                          </div>

                          {/* Document Count */}
                          <div className="col-span-2">
                            <span className="inline-flex items-center px-3 py-1.5 text-sm font-semibold rounded-lg bg-green-100 text-green-800">
                              📎 {attachmentCount}
                            </span>
                          </div>

                          {/* Actions */}
                          <div className="col-span-3 flex items-center justify-end space-x-2">
                            <button
                              onClick={() => handleViewActivity(activity)}
                              className="p-1.5 text-gray-400 hover:text-green-600 hover:bg-green-50 rounded-lg transition-all"
                              title={t("common:view")}
                            >
                              <EyeIcon className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => handleEditActivity(activity)}
                              className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-all"
                              title={t("common:edit")}
                            >
                              <PencilIcon className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => handleDeleteActivity(activity)}
                              className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-all"
                              title={t("common:delete")}
                            >
                              <TrashIcon className="w-4 h-4" />
                            </button>
                          </div>
                        </div>

                        {/* Mobile Layout */}
                        <div className="lg:hidden">
                          <div className="flex items-start gap-2">
                            <div className="flex items-center space-x-3 flex-1 min-w-0">
                              <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center shadow-sm flex-shrink-0">
                                <span className="text-sm">{activityIcon}</span>
                              </div>
                              <div className="flex-1 min-w-0">
                                <h3 className="text-xs font-bold text-slate-800 truncate">
                                  {activity.title}
                                </h3>
                                <div className="flex items-center gap-2 mt-1">
                                  <span className="inline-flex items-center px-2 py-0.5 text-xs font-semibold rounded bg-blue-100 text-blue-800">
                                    📋 {formCount}
                                  </span>
                                  <span className="inline-flex items-center px-2 py-0.5 text-xs font-semibold rounded bg-green-100 text-green-800">
                                    📎 {attachmentCount}
                                  </span>
                                </div>
                              </div>
                            </div>
                          </div>
                          <div className="flex flex-wrap gap-2">
                            {activity.requiresApproval && (
                              <span className="inline-flex items-center px-2 py-1 text-xs font-semibold rounded-full bg-amber-100 text-amber-800">
                                Onay
                              </span>
                            )}
                          </div>
                          <div className="mt-2 flex justify-end gap-1">
                            <button
                              onClick={() => handleViewActivity(activity)}
                              className="inline-flex h-8 w-8 items-center justify-center rounded-md bg-green-50 text-green-700"
                              title={t("common:view")}
                            >
                              <EyeIcon className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => handleEditActivity(activity)}
                              className="inline-flex h-8 w-8 items-center justify-center rounded-md bg-blue-50 text-blue-700"
                              title={t("common:edit")}
                            >
                              <PencilIcon className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => handleDeleteActivity(activity)}
                              className="inline-flex h-8 w-8 items-center justify-center rounded-md bg-red-50 text-red-700"
                              title={t("common:delete")}
                            >
                              <TrashIcon className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              <div className="border-t border-slate-200 bg-slate-50/80 px-4 lg:px-5 py-3">
                <div className="flex items-center justify-between gap-3 flex-wrap">
                  <PaginationControls
                    currentPage={currentPage}
                    totalPages={totalPages}
                    totalItems={filteredActivities.length}
                    itemsPerPage={itemsPerPage}
                    onPageChange={setCurrentPage}
                    accent="blue"
                    className="mt-0"
                  />

                  <div className="ml-auto flex items-center gap-2 text-xs text-slate-600">
                    <span>Göster</span>
                    <select
                      value={itemsPerPage}
                      onChange={(e) => setItemsPerPage(Number(e.target.value))}
                      className="rounded-lg border border-slate-300 bg-white px-2 py-1 text-xs font-medium text-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value={10}>10</option>
                      <option value={20}>20</option>
                      <option value={50}>50</option>
                    </select>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Activity Editor Modal */}
      {showActivityEditor && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50">
          <div className="h-full w-full">
            <ActivityEditor
              activity={
                (editingActivity || viewingActivity) as ActivityDefinition
              }
              onSave={handleSaveActivity}
              onCancel={handleCancelEditor}
              readonly={!!viewingActivity}
            />
          </div>
        </div>
      )}
    </div>
  );
};
